import React, { useContext } from 'react';
import { Route, Router, Switch } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import clsx from 'clsx';
import '@blueprintjs/core/lib/css/blueprint.css';
import '@blueprintjs/icons/lib/css/blueprint-icons.css';
import '@blueprintjs/docs-theme/lib/css/docs-theme.css';
import '@blueprintjs/datetime/lib/css/blueprint-datetime.css';
import '@aws-amplify/ui-react/styles.css';
import '@blueprintjs/table/lib/css/table.css';
import { authStore, mainStoreContext, alertStore, authStoreContext } from './stores';
import { history } from './utils';
import './App.scss';
import { SpinnerContainer } from "./components/global/spinner-container";
import { DialogContainer } from "./components/global/dialog-container";
import { Forbidden } from './pages/forbidden';
import { Login } from './pages/auth/login';
import { Layout } from './pages/layout/layout';
import Maintenance from './pages/maintenance';
import { AlertContainer } from "./components/global/alert-container";
import { OAuth } from "./utils/oauth";
import { Amplify, Auth, Hub } from 'aws-amplify';
import { AuthState, onAuthUIStateChange } from '@aws-amplify/ui-components';
import { withAuthenticator } from '@aws-amplify/ui-react'
import awsmobile from './aws-exports';
import { LogoutToken } from './pages/auth/logout';
import { ls } from './utils/secure-local-storage';
import {Logger as AuditLog} from './utils/logger';
import { appRedirect } from './utils/app-redirect';
import { createMerchantUser } from './services/auth';

Amplify.configure(awsmobile);

OAuth.autoLogin();

const isMaintenance = process.env.REACT_APP_MAINTAINANCE_ENABLE === 'true';

const redirectSignUp = (onClose) => {
    let awsForm = document.querySelector('.amplify-form');
    if (awsForm) {
        const tabs = awsForm.querySelector('.amplify-tabs');
        const signUpForm = tabs.lastChild;
        if (signUpForm) {
            signUpForm.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
        }
    }
    onClose();
}

export const App = observer(() => {
    Hub.listen("auth", ({ payload: { event, data } }) => {
        switch (event) {
            case 'signIn':
                 data.attributes && AuditLog.writeLog(`click - button - Sign in - signed in successfully`, data.attributes.email);
                 authStore.justLoggedIn = true;
                 OAuth.autoLogin();
                break;
            case 'signUp':
                AuditLog.writeLog('click - button - Create Account - signed up successfully', data.user.username);
                break;
            case 'cognitoHostedUI':
                if (data.signInUserSession) {
                    const idPayload = data.signInUserSession.idToken.payload;
                    AuditLog.writeLog('click - button - Sign in with Google - signed in successfully', idPayload.email);
                    authStore.justLoggedIn = true;
                    // Create the merchant user on first Google sign-in (this also
                    // triggers the new-signup notification email). The backend is
                    // idempotent: existing users get a 200 and no email.
                    createMerchantUser({
                        email: idPayload.email || '',
                        name: idPayload.name || '',
                        contact_number: idPayload.phone_number || ''
                    })
                        .catch(() => {})
                        .finally(() => appRedirect("merchant-portal", true));
                }
                break;

            case "signIn_failure":
                authStore.loggedIn = false;
                if (data.message.includes('PreSignUp+failed+with+error+DestinationUser+not+found') || data.message.includes('User does not exist')) {
                    alertStore.add({ text: (onClose) => <>User does not exist. Please <span className='link-style' onClick={() => redirectSignUp(onClose)}>create account</span> to continue.</>, isCreateUser: true });
                }
                else if (data.message.includes('Already+found+an+entry+for+username')) {
                    alertStore.add({ text: 'Google account has been successfully created. Please try again.' });
                }
                else {
                    alertStore.add({ text: 'Login failed' });
                }
                break;

            case "forgotPassword":
                AuditLog.writeLog('click - button - Reset password', data.username);
                break;

            case "forgotPassword_failure":
                // alertStore.add({ text: 'User not found' });
                break;

            case "forgotPasswordSubmit":
                alertStore.add({ text: 'Reset password success' })
                break;
        }
    });

    React.useEffect(() => {
        return onAuthUIStateChange((nextAuthState, authData) => {
            authStore.setAuthState(nextAuthState);
            authStore.setAuthUser(authData);
            if (nextAuthState === AuthState.SignedIn && authData) {
                Auth.currentAuthenticatedUser()
                    .then(user => {
                        authStore.loggedIn = true;
                        ls.remove('emailRegister')
                    })
                    .catch(err => console.log(err));
            }
        });
    }, []);

    function routes() {
        let form = null;
        
        // If maintenance mode is enabled, show maintenance page for all routes except forbidden
        if (isMaintenance) {
            return [<Route key="maintenance" path="/" component={Maintenance} />];
        }
        
        switch (history.location.pathname) {
            case '/logout': {
                LogoutToken();
                break;
            }
            case '/refreshtoken': {
                break;
            }
            default: {
                if (authStore.loggedIn) {
                    if (authStore.justLoggedIn) {
                        return [];
                    }

                    form = [<Route key="layout" path="/" component={Layout} />]
                } else {
                    form = [<Route key="login" path="/login" component={Login} />];
                }
                break;
            }
        }
        return form;
    }

    const mainStore = useContext(mainStoreContext);
    const authStore = useContext(authStoreContext);
    const router = (
        <Router history={history}>
            <Switch>
                <Route path="/forbidden" component={Forbidden} />,
                {routes()}
            </Switch>
        </Router>
    );

    return [
        <div key="main" className={clsx('docs-root', mainStore.themeClass)}>
            <div className="docs-app">
                {router}
            </div>
        </div>,
        <AlertContainer key="alert" className={mainStore.themeClass} />,
        <SpinnerContainer key="spinner" />,
        <DialogContainer key="dialog" className={mainStore.themeClass} />,
        // <iframe key="iframe" src={`${process.env.REACT_APP_DASHBOARD_MERCHANT_URL + `/logintoken`}`} id='iframe'></iframe>
    ];
});

export default withAuthenticator(App)